import L from "leaflet";
import { useEffect, useRef, useState } from "react";
import Select from 'react-select';

import {
    FeatureGroup,
    MapContainer,
    Marker,
    Polygon,
    Popup,
    TileLayer,
    useMap
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useNavigate } from "react-router-dom";

import almusbahLogo from '../assets/almusbahlogo.png';
import codovioLogo from '../assets/codoviologo.png';

import "bootstrap/dist/css/bootstrap.min.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";


const ShopCenterSetter = ({ center }) => {
    const map = useMap();

    useEffect(() => {
        if (center) {
            map.setView(center, 16);
        }
    }, [center, map]);

    return null;
};

// 🔎 SEARCH CONTROL COMPONENT
const SearchControl = ({ onLocationFound }) => {
    const map = useMap();
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const searchLocation = async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
            );
            const results = await response.json();
            setSearchResults(results);
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setIsSearching(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            searchLocation(searchQuery);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleSearchSelect = (result) => {
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        map.setView([lat, lon], 15);
        const marker = L.marker([lat, lon]).addTo(map);
        marker.bindPopup(result.display_name).openPopup();
        setSearchQuery("");
        setSearchResults([]);
        if (onLocationFound) {
            onLocationFound({ lat, lng: lon, name: result.display_name });
        }
    };

    return (
        <div
            className="leaflet-top"
            style={{ marginTop: "10px", marginLeft: "50px", zIndex: 1000 }}
        >
            <div className="leaflet-control bg-white rounded-3 shadow-sm p-2" style={{ width: "280px" }}>
                <div>
                    <input
                        type="text"
                        placeholder="🔍 Search location on map..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="form-control"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery("");
                                setSearchResults([]);
                            }}
                            className="btn btn-sm btn-link position-absolute top-50 end-0 translate-middle-y text-muted"
                        >
                            ×
                        </button>
                    )}
                </div>
                {isSearching && <div className="p-2 small text-muted">🔄 Searching...</div>}
                {searchResults.length > 0 && (
                    <div className="list-group mt-1" style={{ maxHeight: "250px", overflowY: "auto" }}>
                        {searchResults.map((result, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => handleSearchSelect(result)}
                                className="list-group-item list-group-item-action"
                            >
                                <div className="fw-semibold">
                                    📍 {result.name || result.display_name.split(",")[0]}
                                </div>
                                <div className="small text-muted">
                                    {result.display_name}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// 🗺️ MAIN MAP COMPONENT
const AddBranchLocationLayer = () => {
    const navigate = useNavigate();
    const featureGroupRef = useRef();

    const [selectedCenter, setSelectedCenter] = useState(null);


    //  const map = useMap();

    const [mapCenter, setMapCenter] = useState([21.6288, 39.1091]);
    const [polygons, setPolygons] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [submittedPolygon, setSubmittedPolygon] = useState([]);

    const [shops, setShops] = useState([]);
    const [selectedShop, setSelectedShop] = useState(null);
    const [initialPolygon, setInitialPolygon] = useState([]);

    // 📍 Get current user location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setMapCenter([latitude, longitude]);
                },
                (error) => console.log("Location access denied or unavailable:", error)
            );
        }
    }, []);

    // 🔗 Fetch shops on mount
    useEffect(() => {
        const fetchShops = async () => {
            try {
                const res = await fetch('https://apiv2.mawgif.com/mawgifboportalbackend/public/almusbah-shops/polygons');
                const data = await res.json();
                if (data?.status === 'success') {
                    setShops(data.data);
                } else {
                    console.error('Failed to fetch shops');
                }
            } catch (err) {
                console.error('API error:', err);
            }
        };
        fetchShops();
    }, []);

    // ✅ Handle shop dropdown change
    const handleShopChange = (e) => {
        const selectedValue = JSON.parse(e.target.value);

        const shop = shops.find(
            s =>
                s.cs_bus_unit === selectedValue.cs_bus_unit &&
                s.cs_customer === selectedValue.cs_customer &&
                s.cs_shop_code === selectedValue.cs_shop_code
        );

        setSelectedShop(shop);

        if (shop && shop.cs_shop_polygone) {
            setInitialPolygon(shop.cs_shop_polygone);
            const center = getPolygonCenter(shop.cs_shop_polygone);
            setSelectedCenter(center);
        } else {
            setInitialPolygon([]);
            setSelectedCenter(null);
        }
    };

    // ✅ When user draws new polygon
    const handlePolygonCreated = (e) => {
        const layer = e.layer;
        const latlngs = layer.getLatLngs()[0].map((point) => ({
            lat: point.lat,
            lng: point.lng,
        }));
        setPolygons(latlngs);

        setInitialPolygon([]);
    };


    const handlePolygonEdited = (e) => {

        // console.log("handlePolygonEdited")
        // const layer = e.layer;
        // const latlngs = layer.getLatLngs()[0].map((point) => ({
        //     lat: point.lat,
        //     lng: point.lng,
        // }));
        // setPolygons(latlngs);

        // setInitialPolygon([]);  
    };

    const handlePolygonDeleted = (e) => {

        setPolygons([]);
        console.log("deleted")
    };





    // ✅ Form submit
    const handleLocationFormSubmit = async (e) => {
        e.preventDefault();

        e.preventDefault();
        if (polygons.length === 0) {
            alert("❗️ Please draw a polygon on the map before submitting!");
            return;
        }

        if (!selectedShop) {
            alert("❗️ Please select a shop before submitting!");
            return;
        }

        const payload = {
            bus_unit: selectedShop.cs_bus_unit,
            customer: selectedShop.cs_customer,
            shop_code: selectedShop.cs_shop_code,
            polygon_json: polygons
        };

        console.log("✅ Payload to submit:", payload);


        try {
            const response = await fetch('https://apiv2.mawgif.com/mawgifboportalbackend/public/almusbah-shops/update-polygon', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`❗️ Server returned status ${response.status}`);
            }

            const result = await response.json();
            console.log("✅ Server Response:", result);

            alert("✅ Polygon data saved successfully!");

            // Optionally clear drawn polygon
            setPolygons([]);
            setShowModal(false);

navigate('/');  // for home


        } catch (error) {
            console.error("❗️ Error while saving polygon:", error);
            alert("❗️ Failed to save polygon data. Please try again.");
        }



    };

    // ✅ Leaflet custom draw text
    useEffect(() => {
        L.drawLocal.draw.toolbar.buttons.polygon = "Draw a location polygon";
        L.Draw.Polygon.prototype._getTooltipText = function () {
            return {
                text: this._initialLabelText || "Click to start drawing a polygon",
            };
        };
    }, []);

    const getPolygonCenter = (polygon) => {
        if (!polygon || polygon.length === 0) return [0, 0];
        const latSum = polygon.reduce((sum, point) => sum + point.lat, 0);
        const lngSum = polygon.reduce((sum, point) => sum + point.lng, 0);
        return [latSum / polygon.length, lngSum / polygon.length];
    };

    return (
        <div className="container-fluid p-0">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-white shadow-sm">
                <img src={codovioLogo} alt="Codovio Logo" style={{ height: '50px' }} />
                <img src={almusbahLogo} alt="Al Musbah Logo" style={{ height: '50px' }} />
            </div>

            {/* Body */}
            <div className="card radius-12 border-0">
                <div className="card-body p-3">
                    <form onSubmit={handleLocationFormSubmit}>
                        <label className="form-label fw-bold">
                            Search & Draw Location Polygon
                            <small className="text-muted d-block">
                                Use the search box on the map to find a location, then draw a polygon around the area
                            </small>
                        </label>

                        {/* Shop Dropdown */}
                        <Select
                            className="mb-3"
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                            value={
                                selectedShop
                                    ? {
                                        value: JSON.stringify({
                                            cs_bus_unit: selectedShop.cs_bus_unit,
                                            cs_customer: selectedShop.cs_customer,
                                            cs_shop_code: selectedShop.cs_shop_code
                                        }),
                                        label: `${selectedShop.cs_shop_desc1} / ${selectedShop.cs_shop_desc2}`
                                    }
                                    : null
                            }
                            onChange={(selectedOption) => {
                                const selectedValue = JSON.parse(selectedOption.value);
                                const shop = shops.find(
                                    s =>
                                        s.cs_bus_unit === selectedValue.cs_bus_unit &&
                                        s.cs_customer === selectedValue.cs_customer &&
                                        s.cs_shop_code === selectedValue.cs_shop_code
                                );
                                setSelectedShop(shop);
                                if (shop && shop.cs_shop_polygone) {
                                    setInitialPolygon(shop.cs_shop_polygone);
                                    const center = getPolygonCenter(shop.cs_shop_polygone);
                                    setSelectedCenter(center);
                                } else {
                                    setInitialPolygon([]);
                                    setSelectedCenter(null);
                                }
                            }}
                            options={shops.map((shop) => ({
                                value: JSON.stringify({
                                    cs_bus_unit: shop.cs_bus_unit,
                                    cs_customer: shop.cs_customer,
                                    cs_shop_code: shop.cs_shop_code
                                }),
                                label: `${shop.cs_shop_desc1} / ${shop.cs_shop_desc2}`
                            }))}
                        />


                        {/* Map */}
                        <MapContainer center={mapCenter} zoom={13} style={{ height: "65vh", width: "100%" }}

                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <SearchControl />

                            {selectedCenter && <ShopCenterSetter center={selectedCenter} />}


                            <FeatureGroup ref={featureGroupRef}>
                                {initialPolygon && initialPolygon.length > 0 && (
                                    <>
                                        <Polygon
                                            positions={initialPolygon}
                                            pathOptions={{
                                                color: 'green',
                                                weight: 2,
                                                opacity: 0.8,
                                                fillOpacity: 0.6
                                            }}
                                        />
                                        <Marker position={getPolygonCenter(initialPolygon)}>
                                            <Popup>
                                                Selected Shop Center
                                            </Popup>
                                        </Marker>
                                    </>
                                )}

                                <EditControl
                                    position="topright"
                                    onCreated={handlePolygonCreated}
                                    onEdited={handlePolygonEdited}
                                    onDeleted={handlePolygonDeleted}
                                    draw={{
                                        polygon: {
                                            shapeOptions: {
                                                color: "#ff4500",
                                                weight: 3,
                                                opacity: 0.8,
                                                fillColor: "#ff4500",
                                                fillOpacity: 0.5,
                                            },
                                        },
                                        polyline: false,
                                        rectangle: true,
                                        circle: false,
                                        marker: false,
                                        circlemarker: false,
                                    }}
                                    edit={{ remove: true }}
                                />
                            </FeatureGroup>
                        </MapContainer>

                        <div className="d-flex gap-3 mt-4">
                            <button type="submit" className="btn btn-primary">Submit</button>
                            <button type="button" className="btn btn-secondary" onClick={() => navigate("/")}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Submitted Polygon JSON</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                            </div>
                            <div className="modal-body">
                                <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {JSON.stringify(submittedPolygon, null, 2)}
                                </pre>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddBranchLocationLayer;
